import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'

export default function ForgotPassword() {
  const navigate = useNavigate()
  const { forgotPassword, verifyOTP } = useAuth()
  
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [verifying, setVerifying] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [resettingPassword, setResettingPassword] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [resendDisabled, setResendDisabled] = useState(false)
  const [countdown, setCountdown] = useState(30)
  const [otpVerified, setOtpVerified] = useState(false)
  const { resetPassword } = useAuth()

  // ✅ Step 1: Send OTP to email
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address')
      return
    }

    setLoading(true)

    try {
      // ✅ Use the forgotPassword method from AuthContext
      await forgotPassword(email)
      setSubmitted(true)
      console.log('✅ OTP sent to:', email)
    } catch (error: any) {
      console.error('❌ Forgot password error:', error)
      // Show generic message for security
      toast.error('If this email exists, you will receive a password reset code')
      setSubmitted(true)
    } finally {
      setLoading(false)
    }
  }

  // ✅ Resend OTP
  const handleResendOTP = async () => {
    try {
      setResendDisabled(true)
      await forgotPassword(email)
      toast.success('OTP resent to your email!')
      setCountdown(30)
    } catch (error) {
      console.error('❌ Resend OTP error:', error)
      toast.error('Failed to resend OTP')
      setResendDisabled(false)
    }
  }

  // ✅ Countdown timer for resend
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (resendDisabled && countdown > 0) {
      timer = setTimeout(() => setCountdown(prev => prev - 1), 1000)
    } else if (countdown === 0) {
      setResendDisabled(false)
      setCountdown(30)
    }
    return () => clearTimeout(timer)
  }, [resendDisabled, countdown])

  // ✅ Password strength checker
  useEffect(() => {
    if (newPassword) {
      let strength = 0
      if (newPassword.length >= 8) strength++
      if (/[A-Z]/.test(newPassword)) strength++
      if (/[0-9]/.test(newPassword)) strength++
      if (/[^A-Za-z0-9]/.test(newPassword)) strength++
      setPasswordStrength(strength)
    } else {
      setPasswordStrength(0)
    }
  }, [newPassword])

  // ✅ Step 2: Verify OTP
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault()

    const otpString = otp.join('')

    if (otpString.length !== 6) {
      toast.error('Please enter a complete 6-digit OTP')
      return
    }

    setVerifying(true)

    try {
      // ✅ Use the verifyOTP method from AuthContext with 'password-reset' purpose
      const isValid = await verifyOTP(email, otpString, 'password-reset')

      if (isValid) {
        toast.success('OTP verified! Now set your new password.')
        setOtpVerified(true)
        // Scroll to password form
        setTimeout(() => {
          document.getElementById('password-form')?.scrollIntoView({ behavior: 'smooth' })
        }, 100)
      } else {
        toast.error('Invalid or expired OTP')
        // Reset OTP fields
        setOtp(['', '', '', '', '', ''])
      }
    } catch (error: any) {
      console.error('❌ OTP verification error:', error)
      toast.error('Invalid or expired OTP')
      // Reset OTP fields
      setOtp(['', '', '', '', '', ''])
    } finally {
      setVerifying(false)
    }
  }

  // ✅ Step 3: Reset Password
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()

    const otpString = otp.join('')

    // Validate inputs
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }

    if (passwordStrength < 3) {
      toast.error('Please use a stronger password')
      return
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    if (otpString.length !== 6) {
      toast.error('Please verify your OTP first')
      return
    }

    setResettingPassword(true)

    try {
      // ✅ Use the resetPassword method from AuthContext
      await resetPassword(email, otpString, newPassword, confirmPassword)
      toast.success('Password reset successfully!')
      console.log('✅ Password reset for:', email)
      
      // Redirect to login
      setTimeout(() => {
        navigate('/auth?mode=login')
      }, 2000)
    } catch (error: any) {
      console.error('❌ Password reset error:', error)
      toast.error('Failed to reset password')
    } finally {
      setResettingPassword(false)
    }
  }

  // ✅ OTP Input Handler
  const handleOtpChange = (index: number, value: string) => {
    // Allow only numbers
    if (!/^\d*$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value.slice(0, 1) // Ensure only one character
    setOtp(newOtp)

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`)
      nextInput?.focus()
    }
  }

  // ✅ OTP Backspace Handler
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`)
      prevInput?.focus()
    }
  }

  // ✅ Password strength color
  const getPasswordStrengthColor = () => {
    switch(passwordStrength) {
      case 0: return 'bg-gray-200'
      case 1: return 'bg-red-500'
      case 2: return 'bg-yellow-500'
      case 3: return 'bg-green-500'
      case 4: return 'bg-green-600'
      default: return 'bg-gray-200'
    }
  }

  // ✅ Password strength text
  const getPasswordStrengthText = () => {
    switch(passwordStrength) {
      case 0: return 'Enter password'
      case 1: return 'Weak'
      case 2: return 'Fair'
      case 3: return 'Good'
      case 4: return 'Strong'
      default: return ''
    }
  }

  // ✅ Show password reset form if OTP verified
  if (otpVerified) {
    return (
      <div 
        id="password-form"
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg animate-fadeIn">
          <div className="text-center">
            <div className="text-5xl mb-4">🔑</div>
            <h2 className="text-3xl font-bold text-gray-900">Set New Password</h2>
            <p className="text-gray-600 mt-3">
              Create a strong password for your account
            </p>
          </div>

          <form onSubmit={handleResetPassword} className="mt-8 space-y-6">
            {/* New Password */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Create a strong password"
                  minLength={8}
                  disabled={resettingPassword}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                  disabled={resettingPassword}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>

              {/* Password Strength Indicator */}
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${getPasswordStrengthColor()} transition-all duration-300`}
                      style={{ width: `${(passwordStrength / 4) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs ml-2 text-gray-600 min-w-[60px] text-right">
                    {getPasswordStrengthText()}
                  </span>
                </div>
                <ul className="text-xs text-gray-500 grid grid-cols-2 gap-1 mt-2">
                  <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>
                    ✓ At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>
                    ✓ Uppercase letter
                  </li>
                  <li className={/[0-9]/.test(newPassword) ? 'text-green-600' : ''}>
                    ✓ Number
                  </li>
                  <li className={/[^A-Za-z0-9]/.test(newPassword) ? 'text-green-600' : ''}>
                    ✓ Special character
                  </li>
                </ul>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Confirm your password"
                minLength={8}
                disabled={resettingPassword}
              />
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
              )}
            </div>

            {/* Reset Password Button */}
            <button
              type="submit"
              disabled={resettingPassword || passwordStrength < 3 || newPassword !== confirmPassword}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {resettingPassword ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Resetting Password...
                </span>
              ) : 'Reset Password'}
            </button>
          </form>

          <div className="text-center mt-6">
            <Link to="/auth?mode=login" className="text-indigo-600 hover:text-indigo-700 text-sm">
              ← Back to login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // ✅ Show OTP verification form
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg animate-fadeIn">
          <div className="text-center">
            <div className="text-5xl mb-4">📧</div>
            <h2 className="text-3xl font-bold text-gray-900">Verify Your Email</h2>
            <p className="text-gray-600 mt-3">
              We've sent a verification code to<br />
              <span className="font-semibold text-indigo-600">{email}</span>
            </p>
          </div>

          <form onSubmit={handleVerifyOTP} className="mt-8 space-y-6">
            {/* OTP Input Fields */}
            <div className="flex justify-center gap-2">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className="w-12 h-12 text-center text-xl font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  autoFocus={index === 0}
                  disabled={verifying}
                />
              ))}
            </div>

            {/* Verify Button */}
            <button
              type="submit"
              disabled={verifying || otp.some(d => !d)}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {verifying ? 'Verifying...' : 'Verify & Continue'}
            </button>

            {/* Resend OTP */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={resendDisabled || verifying}
                className="text-indigo-600 hover:text-indigo-700 text-sm font-medium disabled:text-gray-400"
              >
                {resendDisabled ? `Resend code in ${countdown}s` : 'Resend code'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setSubmitted(false)
                  setOtp(['', '', '', '', '', ''])
                }}
                className="text-gray-600 hover:text-gray-800 text-sm font-medium"
              >
                ← Change email
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // ✅ Initial email form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg animate-fadeIn">
        <div className="text-center mb-8">
          <div className="text-5xl mb-4">🔐</div>
          <h2 className="text-3xl font-bold text-gray-900">Reset Your Password</h2>
          <p className="text-gray-600 mt-3">
            Enter your email and we'll send you a verification code
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              placeholder="you@example.com"
              autoFocus
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading || !email}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending Code...
              </span>
            ) : 'Send Verification Code'}
          </button>
        </form>

        <div className="text-center mt-6">
          <Link to="/auth?mode=login" className="text-indigo-600 hover:text-indigo-700 text-sm">
            ← Back to login
          </Link>
        </div>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500">
            Remember your password?{' '}
            <Link to="/auth?mode=login" className="text-indigo-600 hover:text-indigo-700 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}